import React, { useState, useEffect } from 'react';
import { getFavoriteAssets, getFavoriteVariables, removeFavoriteAsset, removeFavoriteVariable } from '../utils/favorites';
import { buildApiUrl, API_ENDPOINTS } from '../utils/api';

interface FavoritesPanelProps {
  onAssetSelect: (asset: string) => void;
  onVariableSelect: (variable: string) => void;
}

interface HotItem {
  name: string;
  rank: number;
  heat_score: number;
  source?: string;
}

const FavoritesPanel: React.FC<FavoritesPanelProps> = ({ onAssetSelect, onVariableSelect }) => {
  const [favoriteAssets, setFavoriteAssets] = useState<string[]>([]);
  const [favoriteVariables, setFavoriteVariables] = useState<string[]>([]);
  const [hotAssets, setHotAssets] = useState<HotItem[]>([]);
  const [hotVariables, setHotVariables] = useState<HotItem[]>([]);
  const [loadingHot, setLoadingHot] = useState(false);

  const loadFavorites = () => {
    setFavoriteAssets(getFavoriteAssets());
    setFavoriteVariables(getFavoriteVariables());
  };

  const loadHotTrends = async () => {
    setLoadingHot(true);
    try {
      const [assetsRes, variablesRes] = await Promise.all([
        fetch(buildApiUrl(API_ENDPOINTS.TRENDING_ASSETS)),
        fetch(buildApiUrl(API_ENDPOINTS.TRENDING_VARIABLES))
      ]);
      
      if (assetsRes.ok) {
        const assetsData = await assetsRes.json();
        setHotAssets(assetsData.assets || []);
      }
      
      if (variablesRes.ok) {
        const variablesData = await variablesRes.json();
        setHotVariables(variablesData.variables || []);
      }
    } catch (error) {
      console.error('加载热门趋势失败:', error);
    } finally {
      setLoadingHot(false);
    }
  };

  useEffect(() => {
    loadFavorites();
    loadHotTrends();
    // 监听存储变化（用于跨组件同步）
    const handleStorageChange = () => {
      loadFavorites();
    };
    window.addEventListener('storage', handleStorageChange);
    // 自定义事件，用于同窗口内的组件通信
    window.addEventListener('favoritesUpdated', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('favoritesUpdated', handleStorageChange);
    };
  }, []);

  const handleRemoveAsset = (e: React.MouseEvent, asset: string) => {
    e.stopPropagation();
    removeFavoriteAsset(asset);
    loadFavorites();
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleRemoveVariable = (e: React.MouseEvent, variable: string) => {
    e.stopPropagation();
    removeFavoriteVariable(variable);
    loadFavorites();
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  return (
    <div className="w-64 bg-white rounded-lg shadow-lg p-4 h-fit sticky top-4">
      <h2 className="text-lg font-bold text-gray-800 mb-4">收藏标签池</h2>
      
      {/* 投资标的标签池 */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
          <span className="mr-2">📊</span>
          投资标的
        </h3>
        {favoriteAssets.length === 0 ? (
          <p className="text-xs text-gray-400 italic">暂无收藏</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {favoriteAssets.map((asset) => (
              <div
                key={asset}
                className="group relative inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => onAssetSelect(asset)}
              >
                <span>{asset}</span>
                <button
                  onClick={(e) => handleRemoveAsset(e, asset)}
                  className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 transition-opacity"
                  title="取消收藏"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 变量标签池 */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
          <span className="mr-2">🔍</span>
          变量
        </h3>
        {favoriteVariables.length === 0 ? (
          <p className="text-xs text-gray-400 italic">暂无收藏</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {favoriteVariables.map((variable) => (
              <div
                key={variable}
                className="group relative inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm cursor-pointer hover:bg-green-100 transition-colors"
                onClick={() => onVariableSelect(variable)}
              >
                <span>{variable}</span>
                <button
                  onClick={(e) => handleRemoveVariable(e, variable)}
                  className="opacity-0 group-hover:opacity-100 text-green-500 hover:text-green-700 transition-opacity"
                  title="取消收藏"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 发现热门机会 */}
      <div className="border-t border-gray-200 pt-4 mt-4">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">🔥</span>
          发现热门机会
        </h2>

        {/* 热门投资标的 */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <span className="mr-2">📈</span>
            热门投资标的
            {loadingHot && <span className="ml-2 text-xs text-gray-400">加载中...</span>}
          </h3>
          {hotAssets.length === 0 && !loadingHot ? (
            <p className="text-xs text-gray-400 italic">暂无数据</p>
          ) : (
            <div className="space-y-2">
              {hotAssets.map((asset) => (
                <div
                  key={asset.rank}
                  className="flex items-center gap-2 px-2 py-1.5 bg-gradient-to-r from-orange-50 to-red-50 rounded-md cursor-pointer hover:from-orange-100 hover:to-red-100 transition-colors border border-orange-200"
                  onClick={() => onAssetSelect(asset.name)}
                >
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full">
                    {asset.rank}
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-800">{asset.name}</span>
                  <span className="text-xs text-orange-600 font-semibold">🔥{asset.heat_score}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 热门变量 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <span className="mr-2">📰</span>
            热门财经变量
            {loadingHot && <span className="ml-2 text-xs text-gray-400">加载中...</span>}
          </h3>
          {hotVariables.length === 0 && !loadingHot ? (
            <p className="text-xs text-gray-400 italic">暂无数据</p>
          ) : (
            <div className="space-y-2">
              {hotVariables.map((variable) => (
                <div
                  key={variable.rank}
                  className="flex items-center gap-2 px-2 py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-md cursor-pointer hover:from-purple-100 hover:to-pink-100 transition-colors border border-purple-200"
                  onClick={() => onVariableSelect(variable.name)}
                >
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-purple-500 text-white text-xs font-bold rounded-full">
                    {variable.rank}
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-800">{variable.name}</span>
                  <span className="text-xs text-purple-600 font-semibold">🔥{variable.heat_score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FavoritesPanel;

